if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', class TabsComponent extends HTMLElement {
    constructor() {
      super();
      this.currentTab = null;
      this.tabControls = null;
      this.tabContents = null;
      this.activeTab = null;
      this.activeTabContent = null;
      this.indicator = null;

      this.motion = window.motion || {};

      this.init();
      this.boundResize = this.handleResize.bind(this);
    }

    init() {
      this.setupType();
      this.setupElements();
      this.setupEventListeners();
      this.setInitialTab();
      this.updateTabState();
      this.updateIndicator();
      this.updateDescriptions();
    }

    setupType() {
      if (this.hasAttribute('data-section-tab')) {
        this.componentType = 'section';
      } else {
        this.componentType = this.closest('.product__tabs') ? 'product' : 'slider';
      }
    }

    get typeConfig() {
      const configs = {
        slider: {
          tabSelector: '.tabs__tab',
          contentSelector: '.tabs__tab-content',
          activeClass: 'tabs__tab--active',
          panelActiveClass: 'tabs__tab-content--active'
        },
        section: {
          tabSelector: '.tabs__tab',
          contentSelector: '.tabs__tab-content',
          activeClass: 'tabs__tab--active',
          panelActiveClass: 'tabs__tab-content--active'
        },
        product: {
          tabSelector: '.tabs__nav-item:not(.tabs__indicator)',
          contentSelector: '.tabs__panel',
          activeClass: 'tabs__nav-item--active',
          panelActiveClass: 'tabs__panel--active'
        }
      };
      return configs[this.componentType];
    }

    setupElements() {
      if (this.componentType === 'product') {
        this.createProductStructure();
      }
      const config = this.typeConfig;
      this.tabControls = this.querySelectorAll(config.tabSelector);
      this.tabContents = this.querySelectorAll(config.contentSelector);
      this.indicator = this.querySelector('.tabs__indicator');
    }

    createProductStructure() {
      const navItems = this.querySelectorAll('.tabs__nav-item');
      const panels = this.querySelectorAll('.tabs__panel');
      if (!navItems.length || !panels.length) return;

      const navContainer = this.querySelector('.tabs__nav-wrapper .tabs__nav');
      let contentContainer = this.querySelector('.tabs__content');

      if (!contentContainer) {
        contentContainer = document.createElement('div');
        contentContainer.className = 'tabs__content';
        this.appendChild(contentContainer);
      }

      if (navContainer) navItems.forEach(item => navContainer.appendChild(item));
      panels.forEach(panel => contentContainer.appendChild(panel));
    }

    setupEventListeners() {
      this.tabControls.forEach(tab => {
        tab.addEventListener('click', () => this.handleTabClick(tab));
        
        const hoverHandler = () => {
          this.hydrateTabLazy(tab);

          const config = this.typeConfig;
          if (tab.classList.contains(config.activeClass)) {
            return;
          }
          
          const tabIndex = [...this.tabControls].indexOf(tab);
          const panelElement = this.tabContents[tabIndex];
          panelElement.querySelectorAll('img[loading="lazy"]').forEach(img => {
            img.setAttribute('loading', 'eager');

            const reveal = () => {
              requestAnimationFrame(() => {
                img.classList.add('lazy-loaded');
              });
            };

            if (typeof img.decode === 'function') {
              img.decode().then(reveal).catch(reveal);
            } else {
              reveal();
            }
          });
          tab.removeEventListener('mouseenter', hoverHandler);
        };
        
        tab.addEventListener('mouseenter', hoverHandler);
        tab.addEventListener('touchstart', () => this.hydrateTabLazy(tab), { passive: true, once: true });

        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, tab));
      });
      window.addEventListener('resize', this.boundResize);

      const tabsList = this.querySelector('.tabs__list');
      if (tabsList) {
        tabsList.addEventListener('touchstart', () => {
          this.tabControls.forEach(tab => this.hydrateTabLazy(tab));
        }, { passive: true, once: true });
      }
    }

    hydrateTabLazy(tab) {
      const tabIndex = [...this.tabControls].indexOf(tab);
      const panel = this.tabContents[tabIndex];
      if (!panel) return;

      const lazyEl = panel.querySelector('lazy-section-template[data-tab-lazy]');
      if (lazyEl && !lazyEl.hydrated && typeof lazyEl.hydrate === 'function') {
        lazyEl.hydrate('tab_hover_or_click');
      }
    }

    handleTabKeydown(event, currentTab) {
      const tabs = [...this.tabControls];
      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex === -1) return;

      const isVertical = this.hasAttribute('data-tabs-vertical');
      const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
      const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';

      let newIndex = null;

      switch (event.key) {
        case prevKey:
          newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case nextKey:
          newIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.focusAndActivateTab(tabs[newIndex]);
    }

    focusAndActivateTab(tab) {
      if (!tab) return;
      tab.focus();
      this.handleTabClick(tab);
    }

    updateAriaAndTabindex() {
      this.tabControls.forEach(tab => {
        const isActive = tab === this.activeTab;
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    handleResize() {
      clearTimeout(this._indicatorResizeTimeout);
      this._indicatorResizeTimeout = setTimeout(() => this.updateIndicator(), 100);
    }

    setInitialTab() {
      const config = this.typeConfig;
      
      if (this.componentType === 'slider') {
        this.activeTab = this.querySelector(`.${config.activeClass}`) || this.tabControls[0];
        const tabIndex = [...this.tabControls].indexOf(this.activeTab);
        this.activeTabContent = this.tabContents[tabIndex];
        this.currentTab = this.activeTab?.dataset.tabIndex || String(tabIndex + 1);

        this.tabContents.forEach((content, index) => {
          content.classList.toggle('hidden', index !== tabIndex);
        });
      } else {
        const activeItem = this.querySelector(`.${config.activeClass}`) || this.querySelector(config.tabSelector);
        this.currentTab = activeItem?.getAttribute('data-tab');
        this.activeTab = activeItem;
        this.activeTabContent = this.querySelector(`[data-panel="${this.currentTab}"]`);

        this.activeTab?.classList.add(config.activeClass);

        this.tabContents.forEach(panel => {
          const panelTab = panel.getAttribute('data-panel');
          const isActive = panelTab === this.currentTab;
          panel.classList.toggle('hidden', !isActive);
          panel.classList.toggle(config.panelActiveClass, isActive);
        });
      }
      this.updateViewAllLink();
      this.updateAriaAndTabindex();
    }

    handleTabClick(clickedTab){
      if (clickedTab === this.activeTab) return;

      this.hydrateTabLazy(clickedTab);

      if (this.componentType === 'slider') {
        this.handleSliderTabClick(clickedTab);
      } else {
        this.handleStandardTabClick(clickedTab);
      }
    }

    async handleSliderTabClick(clickedTab) {
      const config = this.typeConfig;
      const prevIndex = [...this.tabControls].indexOf(this.activeTab);
      const newIndex = [...this.tabControls].indexOf(clickedTab);
      const prevContent = this.tabContents[prevIndex];
      const newContent = this.tabContents[newIndex];

      this.activeTab?.classList.remove(config.activeClass);
      this.activeTab = clickedTab;
      this.activeTab.classList.add(config.activeClass);
      this.scrollToActiveTab(clickedTab);

      const isMobile = DeviceUtils.isMobile;

      this.motion.animate(prevContent, {
        opacity: [1, 0],
        transform: isMobile ? ['none'] : ['translateY(0)', 'translateY(5px)']
      }, {duration: 0.1}).finished;

      prevContent.classList.add('hidden');

      this.resetAnimation();

      newContent.classList.remove('hidden');
      newContent.classList.add(config.panelActiveClass);

      const animatedComponent = newContent.closest('animated-stages');
      if (animatedComponent) {
        animatedComponent.resetAnimation?.(newContent.querySelector('[data-animate="stages"]'));
        await new Promise(resolve => requestAnimationFrame(resolve));
      }

      this.motion.animate(newContent, {
        opacity: [0, 1],
        transform: isMobile ? ['none'] : ['translateY(10px)', 'translateY(0)']
      }, {duration: 0.1});

      const tabIndex = [...this.tabControls].indexOf(clickedTab);

      this.tabContents.forEach((content, index) => {
        const isActive = index === tabIndex;
        content.classList.toggle('hidden', !isActive);
        content.classList.toggle(config.panelActiveClass, isActive);
      });

      this.activeTabContent = this.tabContents[tabIndex];
      this.currentTab = clickedTab.dataset.tabIndex || String(tabIndex + 1);
      this.updateViewAllLink();
      document.querySelectorAll('slider-controls[data-clone]').forEach(el => el.sync());

      this.triggerAnimations();
      this.updateDescriptions();
      this.resetSlider();
      this.updateAriaAndTabindex();
    }

    handleStandardTabClick(clickedTab) {
      const config = this.typeConfig;
      const targetTab = clickedTab.getAttribute('data-tab');

      this.tabControls.forEach(nav => nav.classList.remove(config.activeClass));
      this.tabContents.forEach(panel => {
        const panelTab = panel.getAttribute('data-panel');
        const isActive = panelTab === targetTab;
        panel.classList.toggle('hidden', !isActive);
        panel.classList.toggle(config.panelActiveClass, isActive);
      });

      clickedTab.classList.add(config.activeClass);
      const activePanel = this.querySelector(`[data-panel="${targetTab}"]`);

      if (activePanel) {
        this.currentTab = targetTab;
        this.activeTab = clickedTab;
        this.activeTabContent = activePanel;
      }

      this.updateTabState();
      this.triggerAnimations();
      if (this.componentType === 'product') {
        this.updateIndicator();
      }

      this.scrollToActiveTab(clickedTab);
      this.updateAriaAndTabindex();
    }

    scrollToActiveTab(activeTab) {
      if (!activeTab) return;

      const customScrollbar = this.querySelector('custom-scrollbar');

      if (customScrollbar?.hasScroll?.()) {
        customScrollbar.scrollToItem(activeTab, false, true, false);
        return;
      }

      if (DeviceUtils.isMobileSmall) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    updateTabState() {
      if (this.componentType === 'product') {
        this.dispatchEvent(new CustomEvent('tabchange', {
          detail: { activeTab: this.currentTab }
        }));
      }
    }

    updateViewAllLink() {
      if (this.componentType !== 'slider') return;
      const url = this.activeTab?.dataset.url;
      this.querySelectorAll('[data-view-all]').forEach(link => {
        link.href = url || '#';
        link.classList.toggle('hidden', !url);
      });
    }

    updateDescriptions() {
      if (this.componentType !== 'slider') return;

      const descriptionContainer = this.querySelector('.tabs__description');
      const allDescriptions = descriptionContainer?.querySelectorAll('[data-tab-index]');
      if (!allDescriptions?.length) return;

      allDescriptions.forEach(el => {
        const elIndex = el.getAttribute('data-tab-index');
        el.classList.toggle('hidden', elIndex !== this.currentTab);
      });
    }

    resetSlider() {
      this.activeTabContent?.querySelectorAll('product-card').forEach(card => {
        card.updateMoreOptionsCount();
      });
      const firstSlider = this.activeTabContent?.querySelector('[data-tab-slider]');
      if (!firstSlider) return;
      let enableSlider = true;
      if (!DeviceUtils.isMobileSmall && firstSlider.querySelector(':scope > .slider-clip > .slider--mobile')
          || DeviceUtils.isMobileSmall && firstSlider.querySelector(':scope > .slider-clip > .slider--desktop')) {
        enableSlider = false;
      }
      const isInitialized = firstSlider.querySelector(':scope > .slider-clip > .slider--initialized');
      const needsReinit = firstSlider.hasAttribute('data-needs-reinit');

      requestAnimationFrame(() => {
        const slider = firstSlider;

        if (needsReinit || (!isInitialized && enableSlider)) {
          slider.reInit?.(0);
        } else if (isInitialized) {
          slider.updatePosition?.();
          slider.goToSlide?.(0, false);
        }
      });
    }

    triggerAnimations() {
      const animatedComponent = this.activeTabContent?.closest('animated-stages');
      const animatedStage = this.activeTabContent?.querySelector('[data-animate="stages"]');

      if (animatedComponent && animatedStage) {
        animatedComponent.initAnimation?.(animatedStage);
      }
    }

    async resetAnimation() {
      const animated = this.activeTabContent?.closest('animated-stages');
      const target = this.activeTabContent?.querySelector('.slider-list');

      if (animated && target) {
        animated.resetAnimation?.(target);
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
    }

    updateIndicator() {
      if (!this.indicator || !this.activeTab) return;

      const navContainer = this.querySelector('.tabs__nav');
      if (!navContainer) return;

      const containerRect = navContainer.getBoundingClientRect();
      const activeRect = this.activeTab.getBoundingClientRect();
      const scrollLeft = navContainer.scrollLeft || 0;

      const left = activeRect.left - containerRect.left + scrollLeft;
      const width = activeRect.width;

      this.indicator.style.left = `${left}px`;
      this.indicator.style.width = `${width}px`;
    }

    setActiveTab(tabName) {
      let targetTab;
      if (this.componentType === 'slider') {
        targetTab = [...this.tabControls].find(tab =>
          tab.dataset.tabIndex === tabName || String([...this.tabControls].indexOf(tab) + 1) === tabName
        );
      } else {
        targetTab = this.querySelector(`[data-tab="${tabName}"]`);
      }
      if (targetTab) this.handleTabClick(targetTab);
    }

    getCurrentTab() {
      return this.currentTab;
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundResize);
      clearTimeout(this._indicatorResizeTimeout);
    }
  });
}